"use client";

import { c as _c } from "react/compiler-runtime";
import { IconAlertInfoOutline18 } from "nucleo-ui-outline-18";
import { CaddieAvatar } from "@/components/caddie/CaddieAvatar";
import { Button } from "@/components/ui/button";
import { FIX_WITH_CADDIE_LABEL } from "@/lib/caddie";
import { useAIChatStore } from "@/stores/aiChatStore";
import type { ValidationError } from "@/lib/workflow";
import { NodeToolbar } from "@xyflow/react";
import { useEffect, useRef, useState, type RefObject } from "react";
import { useNodeToolbarPlacement } from "./useNodeToolbarPlacement";
const SINGLE_ERROR_TOOLBAR_OFFSET = 20;
const MULTIPLE_ERRORS_TOOLBAR_OFFSET = 28;

/** Grace period before hiding so the user can move their mouse into the toolbar. */
const HIDE_DELAY_MS = 400;
interface NodeValidationErrorToolbarProps {
  errors: ValidationError[];
  isVisible: boolean;
  nodeName?: string;
  nodeId: string;
  nodeRef?: RefObject<HTMLDivElement | null>;
  onDeselectNode?: () => void;
}

/**
 * NodeValidationErrorToolbar - Shows validation errors above a node
 * Displayed when the node has validation errors and is hovered or selected
 * Includes "Fix with AI" button to send error context to the AI assistant
 */
export function NodeValidationErrorToolbar(t0) {
  const $ = _c(40);
  const {
    errors,
    isVisible: parentVisible,
    nodeName,
    nodeId,
    nodeRef,
    onDeselectNode
  } = t0;
  const openAIPanel = useAIChatStore(_temp);
  const setPendingMessage = useAIChatStore(_temp2);
  const [isToolbarHovered, setIsToolbarHovered] = useState(false);
  const [showToolbar, setShowToolbar] = useState(false);
  const hideTimeoutRef = useRef(undefined);
  const offset = errors.length > 1 ? MULTIPLE_ERRORS_TOOLBAR_OFFSET : SINGLE_ERROR_TOOLBAR_OFFSET;
  let t1;
  if ($[0] !== nodeRef || $[1] !== offset || $[2] !== showToolbar) {
    t1 = {
      isVisible: showToolbar,
      nodeRef,
      offset
    };
    $[0] = nodeRef;
    $[1] = offset;
    $[2] = showToolbar;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    bridgeClassName,
    enterAnimationClassName,
    position,
    toolbarRef
  } = useNodeToolbarPlacement(t1);
  let t2;
  let t3;
  if ($[4] !== isToolbarHovered || $[5] !== parentVisible) {
    t2 = () => {
      clearTimeout(hideTimeoutRef.current);
      if (parentVisible || isToolbarHovered) {
        hideTimeoutRef.current = setTimeout(() => setShowToolbar(true), 0);
      } else {
        hideTimeoutRef.current = setTimeout(() => setShowToolbar(false), HIDE_DELAY_MS);
      }
      return () => clearTimeout(hideTimeoutRef.current);
    };
    t3 = [parentVisible, isToolbarHovered];
    $[4] = isToolbarHovered;
    $[5] = parentVisible;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  if (errors.length === 0 || !showToolbar) {
    return null;
  }
  let t4;
  if ($[8] !== errors || $[9] !== nodeId || $[10] !== nodeName || $[11] !== onDeselectNode || $[12] !== openAIPanel || $[13] !== setPendingMessage) {
    t4 = () => {
      onDeselectNode?.();
      const errorSummary = errors.map(_temp3).join("\n");
      const message = nodeName ? `Fix validation errors in my ${nodeName} (ID: ${nodeId}):\n${errorSummary}` : `Fix validation errors in node ${nodeId}:\n${errorSummary}`;
      setPendingMessage(message);
      openAIPanel();
    };
    $[8] = errors;
    $[9] = nodeId;
    $[10] = nodeName;
    $[11] = onDeselectNode;
    $[12] = openAIPanel;
    $[13] = setPendingMessage;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const handleFixWithAI = t4;
  let t5;
  let t6;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => setIsToolbarHovered(true);
    t6 = () => setIsToolbarHovered(false);
    $[15] = t5;
    $[16] = t6;
  } else {
    t5 = $[15];
    t6 = $[16];
  }
  let t7;
  if ($[17] !== bridgeClassName) {
    t7 = <div className={bridgeClassName} />;
    $[17] = bridgeClassName;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const t8 = `animate-in fade-in ${enterAnimationClassName} shadow-ds-elev-2 w-full max-w-[320px] rounded-xl border border-red-300 bg-red-50/90 p-3 px-4 backdrop-blur-md duration-200 dark:border-red-800 dark:bg-red-950/80`;
  let t9;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <IconAlertInfoOutline18 className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600 dark:text-red-400" />;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== errors) {
    t10 = <div className="flex items-start gap-2">{t9}<div className="min-w-0 flex-1">{errors.length > 1 ? <><p className="text-xs font-medium text-red-700 dark:text-red-300">{errors.length} validation errors</p><ul className="mt-1 max-h-20 space-y-0.5 overflow-y-auto">{errors.map(_temp4)}</ul></> : <p className="text-xs text-red-600 dark:text-red-400">{errors[0].field && <span className="font-mono text-red-700 dark:text-red-300">{errors[0].field}: </span>}{errors[0].message}</p>}</div></div>;
    $[20] = errors;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== handleFixWithAI) {
    t11 = e_0 => {
      e_0.stopPropagation();
      handleFixWithAI();
    };
    $[22] = handleFixWithAI;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <CaddieAvatar size={16} mood="repair" />;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t11) {
    t13 = <div className="mt-2 flex justify-end border-t border-red-200 pt-2 dark:border-red-800"><Button onClick={t11} className="gap-1.5 px-3 py-1.5 text-xs">{t12}{FIX_WITH_CADDIE_LABEL}</Button></div>;
    $[25] = t11;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t10 || $[28] !== t13 || $[29] !== t8) {
    t14 = <div className={t8}>{t10}{t13}</div>;
    $[27] = t10;
    $[28] = t13;
    $[29] = t8;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t14 || $[32] !== t7 || $[33] !== toolbarRef) {
    t15 = <div ref={toolbarRef} className="relative" onMouseEnter={t5} onMouseLeave={t6}>{t7}{t14}</div>;
    $[31] = t14;
    $[32] = t7;
    $[33] = toolbarRef;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== offset || $[36] !== position || $[37] !== showToolbar || $[38] !== t15) {
    t16 = <NodeToolbar isVisible={showToolbar} position={position} align="center" offset={offset}>{t15}</NodeToolbar>;
    $[35] = offset;
    $[36] = position;
    $[37] = showToolbar;
    $[38] = t15;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  return t16;
}
function _temp4(error, i) {
  return <li key={i} className="text-xs text-red-600 dark:text-red-400">{error.field && <span className="font-mono text-red-700 dark:text-red-300">{error.field}: </span>}{error.message}</li>;
}
function _temp3(e) {
  return `- ${e.message}${e.field ? ` (field: ${e.field})` : ""}`;
}
function _temp2(state_0) {
  return state_0.setPendingMessage;
}
function _temp(state) {
  return state.openPanel;
}